/**
 * CaseManageSlice
 * 
 * Redux slice for managing case management state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - extraReducers.ts: API endpoint matchers
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, CaseManageSliceState } from './state';
import { buildCaseManageExtraReducers } from './extraReducers';

// Import all reducers (renamed to avoid conflicts with exported actions)
import {
  updateCaseManage as updateCaseManageReducer,
  resetCase as resetCaseReducer,
  updateClinicalReviewDetails as updateClinicalReviewDetailsReducer,
  updateDocumentRequest as updateDocumentRequestReducer,
  addNote as addNoteReducer,
  updateNote as updateNoteReducer,
  removeNote as removeNoteReducer,
  addCallLog as addCallLogReducer,
  updateCallLog as updateCallLogReducer,
  removeCallLog as removeCallLogReducer,
  markFieldAsEdited as markFieldAsEditedReducer,
  clearEditedFields as clearEditedFieldsReducer,
  markAsSaved as markAsSavedReducer,
  discardChanges as discardChangesReducer,
  setValidationError as setValidationErrorReducer,
  clearValidationError as clearValidationErrorReducer,
  clearAllValidationErrors as clearAllValidationErrorsReducer,
} from './reducers';

/**
 * Case manage slice definition
 */
const caseManageSlice = createSlice({
  name: 'caseManage',
  initialState,
  reducers: {
    // Core reducers
    updateCaseManage: updateCaseManageReducer,
    resetCase: resetCaseReducer,

    // Clinical review reducers
    updateClinicalReviewDetails: updateClinicalReviewDetailsReducer,

    // Document request reducers
    updateDocumentRequest: updateDocumentRequestReducer,

    // Notes reducers
    addNote: addNoteReducer,
    updateNote: updateNoteReducer,
    removeNote: removeNoteReducer,

    // Call logs reducers
    addCallLog: addCallLogReducer,
    updateCallLog: updateCallLogReducer,
    removeCallLog: removeCallLogReducer,

    // Edit tracking reducers
    markFieldAsEdited: markFieldAsEditedReducer,
    clearEditedFields: clearEditedFieldsReducer,
    markAsSaved: markAsSavedReducer,
    discardChanges: discardChangesReducer,

    // Validation reducers
    setValidationError: setValidationErrorReducer,
    clearValidationError: clearValidationErrorReducer,
    clearAllValidationErrors: clearAllValidationErrorsReducer,
  },
  extraReducers: buildCaseManageExtraReducers,
});

// Export actions
export const caseManageActions = caseManageSlice.actions;

// Export individual actions with Action suffix for convenience
export const {
  updateCaseManage: updateCaseManageAction,
  resetCase: resetCaseAction,
  updateClinicalReviewDetails: updateClinicalReviewDetailsAction,
  updateDocumentRequest: updateDocumentRequestAction,
  addNote: addNoteAction,
  updateNote: updateNoteAction,
  removeNote: removeNoteAction,
  addCallLog: addCallLogAction,
  updateCallLog: updateCallLogAction,
  removeCallLog: removeCallLogAction,
  markFieldAsEdited: markFieldAsEditedAction,
  clearEditedFields: clearEditedFieldsAction,
  markAsSaved: markAsSavedAction,
  discardChanges: discardChangesAction,
  setValidationError: setValidationErrorAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
} = caseManageSlice.actions;

// Also export with original names for backward compatibility
export const {
  updateCaseManage,
  resetCase,
  updateClinicalReviewDetails,
  updateDocumentRequest,
  addNote,
  updateNote,
  removeNote,
  addCallLog,
  updateCallLog,
  removeCallLog,
  markFieldAsEdited,
  clearEditedFields,
  markAsSaved,
  discardChanges,
  setValidationError,
  clearValidationError,
  clearAllValidationErrors,
} = caseManageSlice.actions;

// Export reducer
export default caseManageSlice.reducer;

// Re-export state types
export type { CaseManageSliceState } from './state';

// Re-export selectors
export * from './selectors';
